import { SxProps, Theme } from '@mui/material';

import {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH,
} from '@watershed/shared-universal/utils/constants';
import mixinSx from '@watershed/style/mixinSx';
import { sharedShadow, sxType } from '@watershed/style/styleUtils';

import { transition } from '../utils';

export const SKIP_NAV_LINK_CLASS = 'skip-nav-link';

export const getSharedSidebarStyles = (theme: Theme, isLightTheme: boolean) => {
  const DARK_THEME_COLORS = {
    BACKGROUND: theme.palette.background.dark,
    BACKGROUND_ACTIVE: theme.palette.grey80,
    SCROLLBAR: theme.palette.grey30,
    TEXT_PRIMARY: theme.palette.white,
    TEXT_SECONDARY: theme.palette.grey30,
    TEXT_DISABLED: theme.palette.grey50,
    FOCUS: theme.palette.cobalt40,
    ICON_HOVER: theme.palette.grey80,
    ICON_FOCUS: theme.palette.grey70,
    BORDER: theme.palette.grey70,
    LOGO_COLOR: 'periwinkle',
    WORDMARK_COLOR: theme.palette.white,
  };
  const LIGHT_THEME_COLORS = {
    BACKGROUND: theme.palette.grey10,
    BACKGROUND_ACTIVE: theme.palette.grey20,
    SCROLLBAR: theme.palette.grey30,
    TEXT_PRIMARY: theme.palette.grey70,
    TEXT_SECONDARY: theme.palette.grey50,
    TEXT_DISABLED: theme.palette.grey50,
    FOCUS: 'transparent',
    ICON_HOVER: theme.palette.grey20,
    ICON_FOCUS: theme.palette.grey30,
    BORDER: theme.palette.grey30,
    LOGO_COLOR: 'periwinkle',
    WORDMARK_COLOR: theme.palette.midnight,
  };
  const COLORS = isLightTheme ? LIGHT_THEME_COLORS : DARK_THEME_COLORS;
  const BOX_SHADOW_FOCUS = `0 0 0 2px ${COLORS.FOCUS}`;

  // See https://github.com/reach/reach-ui/blob/main/packages/skip-nav/styles.css for reference.
  const skipNavLinkSxProps: SxProps<Theme> = (theme) => ({
    [`& .${SKIP_NAV_LINK_CLASS}`]: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: '1px',
      width: '1px',
      margin: '-1px',
      padding: 0,
      overflow: 'hidden',
      position: 'absolute',
      color: COLORS.TEXT_PRIMARY,
      '&:focus': {
        padding: theme.spacing(1, 2),
        position: 'fixed',
        zIndex: 99999,
        top: theme.spacing(1),
        left: theme.spacing(1),
        width: 'auto',
        height: 'auto',
        clip: 'auto',
        textDecoration: 'none',
        outline: 'none',
        borderRadius: 1,
        backgroundColor: COLORS.BACKGROUND,
        boxShadow: BOX_SHADOW_FOCUS,
      },
    },
  });

  return {
    COLORS,
    BOX_SHADOW_FOCUS,

    rootSxProps: sxType({
      zIndex: 50,
      gridColumn: 1,
      position: 'fixed',
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      colorScheme: isLightTheme ? 'light' : 'dark',
      backgroundColor: COLORS.BACKGROUND,
      overflow: 'hidden',
      transition: transition('width', 'box-shadow'),
      '@media print': { display: 'none' },
    }),

    logoBarSxProps: mixinSx(
      {
        padding: 0.5,
        height: 56,
        paddingInlineEnd: 2,
        display: 'flex',
        alignItems: 'center',
        transition: transition('translate'),
      },
      skipNavLinkSxProps
    ),

    collapseButtonWrapperSxProps: sxType({
      marginLeft: 'auto',
      position: 'relative',
      right: -4,
    }),
    collapseButtonSxProps: sxType({
      color: COLORS.TEXT_PRIMARY,
      transition: transition('opacity'),
      padding: '4px',
      '&:focus': {
        backgroundColor: 'transparent',
        boxShadow: 'none',
      },
      '&:focus-visible': {
        boxShadow: BOX_SHADOW_FOCUS,
      },
    }),

    iconButtonSxProps: sxType({
      marginInlineStart: 'auto',
      minWidth: 'unset',
      width: '24px',
      height: '24px',
      aspectRatio: '1 / 1',
      color: COLORS.TEXT_PRIMARY,
      transition: (theme) =>
        theme.transitions.create(['opacity', 'background-color'], {
          duration: theme.transitions.duration.short,
        }),
      '&:hover, &:focus': {
        backgroundColor: COLORS.ICON_HOVER,
      },
      'a &:hover, a &:focus-visible': {
        backgroundColor: COLORS.ICON_FOCUS,
      },
      '&:focus-visible': {
        boxShadow: BOX_SHADOW_FOCUS,
      },
    }),

    menuPaperSxProps: sxType({
      transition:
        transition('opacity', 'translate', 'transform') + ' !important',
      width: 228,
      backgroundColor: COLORS.BACKGROUND,
      border: isLightTheme
        ? (theme) => `0.5px solid ${theme.palette.grey30}`
        : undefined,
      borderRadius: '10px',
      padding: 0.5,
      marginInlineStart: 2,
      marginBlockStart: -1.5,
      boxShadow: sharedShadow,
      // When nav appears collapsed, menu should always hug nav
      // Using translate because transform already handles a scale animation
      // and we don't want them to conflict
      '&[data-appears-collapsed="true"]': {
        translate: `-${SIDEBAR_EXPANDED_WIDTH - SIDEBAR_COLLAPSED_WIDTH}px 0`,
      },
      // This is a hidden element that ensures that the menu remains open,
      // when the mouse transitions from the nav to the menu.
      '& :before': {
        position: 'absolute',
        left: -24,
        top: 0,
        content: '""',
        width: '40px',
        userSelect: 'none',
        height: '100%',
      },
      '& .MuiMenuItem-root': {
        color: COLORS.TEXT_PRIMARY,
        gap: 1,
        border: 0,
        paddingBlock: 1,
        paddingInline: 1.5,
        borderRadius: 1,
        '&:hover, &:focus, &:active': {
          backgroundColor: COLORS.BACKGROUND_ACTIVE,
        },
        '& svg': {
          color: COLORS.TEXT_SECONDARY,
          width: 16,
          height: 16,
        },
        '& span': {
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        },
      },
    }),
  };
};
